import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type FactoryPreset = components['schemas']['FactoryPresetOut']
export type FactoryPresetCreate = components['schemas']['FactoryPresetCreateIn']
export type FactoryPresetUpdate = components['schemas']['FactoryPresetUpdateIn']
export type ResolvedPreset = components['schemas']['ResolvedPresetOut']

/** Owner-scoped list of presets for a single factory. Cached aggressively
 * — invalidated by every CUD mutation here and by the channel-config
 * patch in scheduler.ts when ``preset_id`` flips. */
export function useFactoryPresets(factory: string) {
  return useQuery({
    queryKey: queryKeys.factoryPresets.list(factory),
    queryFn: async (): Promise<FactoryPreset[]> => {
      const { data } = await api.GET('/api/v1/factories/{factory}/presets', {
        params: { path: { factory } },
      })
      if (!data) throw new Error('factory presets list empty')
      return data.data
    },
    enabled: Boolean(factory),
    staleTime: 30_000,
  })
}

/** What the cron would actually use for ``slug`` running ``factory``:
 * the channel's own preset, the default-channel's inherited preset, or
 * none (caller falls back to factory_params). */
export function useResolvedChannelPreset(slug: string, factory: string) {
  return useQuery({
    queryKey: queryKeys.scheduler.resolvedPreset(slug, factory),
    queryFn: async (): Promise<ResolvedPreset> => {
      const { data } = await api.GET('/api/v1/scheduler/channels/{slug}/resolved-preset', {
        params: { path: { slug }, query: { factory } },
      })
      if (!data) throw new Error('resolved preset empty')
      return data.data
    },
    enabled: Boolean(slug) && Boolean(factory),
    staleTime: 5_000,
  })
}

export function useCreateFactoryPreset(factory: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: FactoryPresetCreate): Promise<FactoryPreset> => {
      const { data } = await api.POST('/api/v1/factories/{factory}/presets', {
        params: { path: { factory } },
        body: input,
      })
      if (!data) throw new Error('preset create empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.factoryPresets.list(factory) })
      // Resolved view may flip if the new preset becomes the channel default
      // through a follow-up mutation, so invalidate the whole branch.
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
    },
  })
}

export function useUpdateFactoryPreset(factory: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { presetId: string; body: FactoryPresetUpdate }): Promise<FactoryPreset> => {
      const { data } = await api.PATCH('/api/v1/factories/{factory}/presets/{preset_id}', {
        params: { path: { factory, preset_id: input.presetId } },
        body: input.body,
      })
      if (!data) throw new Error('preset patch empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.factoryPresets.list(factory) })
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
    },
  })
}

export function useDeleteFactoryPreset(factory: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (presetId: string): Promise<void> => {
      await api.DELETE('/api/v1/factories/{factory}/presets/{preset_id}', {
        params: { path: { factory, preset_id: presetId } },
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.factoryPresets.list(factory) })
      // Channels referencing this preset_id will fall back via SET NULL.
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
    },
  })
}
